import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import apiService from '../services/api';
import { isMobileDevice } from '../utils/deviceDetection';

interface SchwabAuthStatus {
  authenticated: boolean;
  tokenExpired: boolean;
  expiresAt?: string;
}

interface SchwabAuthProps {
  onAuthStatusChange?: (status: SchwabAuthStatus) => void;
}

const SchwabAuth: React.FC<SchwabAuthProps> = ({ onAuthStatusChange }) => {
  const [authStatus, setAuthStatus] = useState<SchwabAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const popupIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const checkAuthStatus = useCallback(async () => {
    try {
      setLoading(true);
      const status = await apiService.getSchwabAuthStatus();

      // Show success message if status changed from not authenticated to authenticated
      setAuthStatus((prev) => {
        if (status.authenticated && !prev?.authenticated) {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 5000); // Hide after 5 seconds
        }
        return status;
      });

      // Notify parent component of status change
      if (onAuthStatusChange) {
        onAuthStatusChange(status);
      }
    } catch (error) {
      console.error('Failed to check Schwab auth status:', error);
      const errorStatus = {
        authenticated: false,
        tokenExpired: false,
      };
      setAuthStatus(errorStatus);

      // Notify parent component of status change
      if (onAuthStatusChange) {
        onAuthStatusChange(errorStatus);
      }
    } finally {
      setLoading(false);
    }
  }, [onAuthStatusChange]);

  useEffect(() => {
    checkAuthStatus();

    // Set up periodic refresh every 30 seconds to check for auth status changes
    const interval = setInterval(checkAuthStatus, 30000);

    // Check auth status when window regains focus (user returns from OAuth)
    const handleFocus = () => {
      checkAuthStatus();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      // Clean up popup monitoring interval if component unmounts
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
      }
    };
  }, [checkAuthStatus]);

  const handleAuthClick = async () => {
    try {
      setAuthLoading(true);
      const { authUrl } = await apiService.authorizeSchwab();

      // Detect mobile device to determine appropriate auth flow
      const isMobile = isMobileDevice();

      if (isMobile) {
        // Use same window redirect for mobile devices
        // iPhone and other mobile browsers don't support popup windows reliably
        // Note: No need to reset authLoading here as we're navigating away
        window.location.href = authUrl;
      } else {
        // Try popup for desktop, with fallback to redirect
        const popup = window.open(
          authUrl,
          'schwab-auth',
          'width=600,height=700,scrollbars=yes',
        );

        // Check if popup was blocked or failed to open
        // Note: popup.closed check might not catch all popup blocking scenarios immediately
        // but will catch most cases. The worst case is a brief loading state before redirect.
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          // Popup was blocked, fallback to same-window redirect
          // Note: No need to reset authLoading here as we're navigating away
          window.location.href = authUrl;
        } else {
          // Monitor popup for completion
          // Store interval in ref so it can be cleaned up on unmount
          popupIntervalRef.current = setInterval(() => {
            if (popup.closed) {
              if (popupIntervalRef.current) {
                clearInterval(popupIntervalRef.current);
                popupIntervalRef.current = null;
              }
              checkAuthStatus();
              setAuthLoading(false);
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to get Schwab auth URL:', error);
      alert('Failed to start Schwab authentication. Please try again.');
      setAuthLoading(false);
    }
  };

  const handleRevokeClick = async () => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect your Schwab account? This will remove all authentication tokens.',
      )
    ) {
      return;
    }

    try {
      setRevokeLoading(true);
      await apiService.revokeSchwabAuth();
      await checkAuthStatus(); // Refresh status after revoking
    } catch (error) {
      console.error('Failed to revoke Schwab auth:', error);
      alert('Failed to disconnect Schwab account. Please try again.');
    } finally {
      setRevokeLoading(false);
    }
  };

  const formatExpiryDate = (expiresAt?: string) => {
    if (!expiresAt) return 'Unknown';
    const date = new Date(expiresAt);
    return date.toLocaleString();
  };

  const getStatusIcon = () => {
    if (loading) {
      return <RefreshCw className="w-5 h-5 animate-spin text-gray-500" />;
    }

    if (!authStatus?.authenticated) {
      return <ShieldOff className="w-5 h-5 text-red-500" />;
    }

    if (authStatus.tokenExpired) {
      return <ShieldOff className="w-5 h-5 text-orange-500" />;
    }

    return <ShieldCheck className="w-5 h-5 text-green-500" />;
  };

  const getStatusText = () => {
    if (loading) return 'Checking status...';

    if (!authStatus?.authenticated) {
      return 'Not authenticated';
    }

    if (authStatus.tokenExpired) {
      return 'Token expired';
    }

    return 'Authenticated';
  };

  const getStatusColor = () => {
    if (loading) return 'text-gray-500';

    if (!authStatus?.authenticated) {
      return 'text-red-600';
    }

    if (authStatus.tokenExpired) {
      return 'text-orange-600';
    }

    return 'text-green-600';
  };

  const getButtonText = () => {
    if (authLoading) return 'Opening Schwab...';

    if (!authStatus?.authenticated) {
      return 'Connect Schwab Account';
    }

    if (authStatus.tokenExpired) {
      return 'Re-authenticate with Schwab';
    }

    return 'Reconnect Schwab Account';
  };

  const isButtonDisabled = () => {
    return loading || authLoading;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <Shield className="w-5 h-5 mr-2" />
          Schwab Authentication
        </h2>
        <button
          onClick={checkAuthStatus}
          disabled={loading}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {/* Status Display */}
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div>
            <p className={`font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </p>
            {authStatus?.authenticated && authStatus.expiresAt && (
              <p className="text-sm text-gray-500">
                Expires: {formatExpiryDate(authStatus.expiresAt)}
              </p>
            )}
          </div>
        </div>

        {/* Authentication Button */}
        <button
          onClick={handleAuthClick}
          disabled={isButtonDisabled()}
          className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
            authStatus?.authenticated && !authStatus.tokenExpired
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-green-600 hover:bg-green-700'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {authLoading && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <ExternalLink className="w-4 h-4 mr-2" />
          {getButtonText()}
        </button>

        {/* Revoke Button - Only show when authenticated */}
        {authStatus?.authenticated && !loading && (
          <button
            onClick={handleRevokeClick}
            disabled={revokeLoading}
            className="w-full flex items-center justify-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {revokeLoading && (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            )}
            <ShieldOff className="w-4 h-4 mr-2" />
            Disconnect Schwab Account
          </button>
        )}

        {/* Status Messages */}
        {!authStatus?.authenticated && !loading && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Action Required:</strong> Connect your Schwab account to
              access trading data and account information.
            </p>
          </div>
        )}

        {authStatus?.tokenExpired && !loading && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
            <p className="text-sm text-orange-800">
              <strong>Token Expired:</strong> Your Schwab authentication has
              expired. Please re-authenticate to continue.
            </p>
          </div>
        )}

        {authStatus?.authenticated && !authStatus.tokenExpired && !loading && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">
              <strong>Connected:</strong> Your Schwab account is properly
              authenticated and ready to use.
            </p>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md animate-pulse">
            <p className="text-sm text-green-800">
              <strong>🎉 Success!</strong> Your Schwab account has been
              successfully connected.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchwabAuth;
